import React from "react";
import { NftOwnership } from "@devour/client";
import useCreateRefreshNftOwnerships from "@/hooks/useCreateRefreshNftOwnerships";
import { MdRefresh } from "react-icons/md";
import { FiChevronRight, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import AnimateHeight from "react-animate-height";
import GoFrensNftCard from "@/components/goFrens/GoFrensNftCard";
import classNames from "classnames";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import "./MyPOPBadgesCockpit.scss";

interface MyPOPBadgesCockpitProps {
    badges: Array<NftOwnership>;
    className?: string;
    onViewAll?: () => void;
}

export default function MyPOPBadgesCockpit({
    badges,
    className,
    onViewAll,
}: MyPOPBadgesCockpitProps) {
    const { isRefreshing, refreshNftOwnerships } = useCreateRefreshNftOwnerships();
    const { isExpanded, toggleCard } = useCockpitPreferences();
    const expanded = isExpanded("my-pop-badges", false); // Default to collapsed

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard("my-pop-badges");
        }
    };

    return (
        <div className={classNames("my-pop-badges-cockpit", className, { expanded, collapsed: !expanded })}>
            {/* Header */}
            <div
                className="my-pop-badges-cockpit__header"
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="my-pop-badges-content"
            >
                <div className="header-left">
                    <IoShieldCheckmarkOutline className="icon" />
                    <h4>POP Badges</h4>
                    <span className="count">{badges?.length || 0}</span>
                </div>
                <button
                    className="my-pop-badges-cockpit__collapse-toggle"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
            </div>

            <AnimateHeight
                id="my-pop-badges-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className="my-pop-badges-cockpit__content-wrapper">
                    {/* Refresh Button */}
                    <div className="my-pop-badges-cockpit__actions">
                        <button
                            className="refresh-btn"
                            onClick={async () => await refreshNftOwnerships(true)}
                            disabled={isRefreshing}
                        >
                            <MdRefresh className={classNames({ spinning: isRefreshing })} />
                            <span>{isRefreshing ? "Loading..." : "Refresh"}</span>
                        </button>
                    </div>

                    {/* Badges Grid */}
                    <div className="my-pop-badges-cockpit__content">
                        {badges?.length > 0
                            ? <div className="badges-grid">
                                {badges.map(ownership =>
                                    <GoFrensNftCard
                                        key={ownership?.id}
                                        ownership={ownership}
                                        className="badge-card"
                                    />)}
                            </div>
                            : <div className="empty-state">
                                <IoShieldCheckmarkOutline className="empty-icon" />
                                <span className="empty-text">No POP badges yet</span>
                                <span className="empty-subtext">
                                    Complete quests and challenges to earn badges
                                </span>
                            </div>
                        }
                    </div>
                </div>
            </AnimateHeight>
        </div>
    );
}